<template>
  <div
    id="instacart-account"
    class="page-content"
  >
    <div class="card create-update-instacart-account">
      <div class="card-header">
        <h3 class="card-title">
          Instacart Account Information
        </h3>
      </div>
      <hr class="card-title-line">
      <div class="card-content">
        <form
          :id="pageId"
          method="post"
          class="form-horizontal"
          autocomplete="off"
          :class="pageId"
          @submit.prevent="submitForm"
        >
          <fieldset>
            <div class="row">
              <label class="col-sm-3 control-label pt-10">Email*</label>
              <div class="col-sm-3">
                <email-input
                  v-model="instacartInfo.email"
                  required
                  :disabled="!!instacartEmail"
                  @update-validation="updateEmailValidation"
                />
              </div>
              <label class="col-sm-2 control-label pt-10">Password*</label>
              <div class="col-sm-3">
                <sanitized-input
                  id="password"
                  v-model="instacartInfo.password"
                  :required="!backupInstacartAccount"
                  :placeholder="backupInstacartAccount ? '********' : ''"
                  autocomplete="new-password"
                />
              </div>
            </div>
            <div class="row align-items-center">
              <label class="col-sm-3 control-label">Phone Number</label>
              <phone-number-input
                id="phone-number"
                v-model="instacartInfo.phoneNumber"
                class="col-sm-3"
                :page-id="pageId"
              />
              <div class="col-sm-4 offset-2">
                <el-checkbox
                  id="isRescanCanceledGrocery"
                  v-model="instacartInfo.isActive"
                >
                  Is Active Account
                </el-checkbox>
              </div>
            </div>
            <div class="row align-items-center">
              <label class="col-sm-3 control-label">Country</label>
              <div class="col-sm-4">
                <dropdown-list
                  v-model="instacartInfo.country"
                  :options="countryList"
                />
              </div>
              <div class="col-sm-4">
                <el-checkbox
                  id="isEbtAccount"
                  v-model="instacartInfo.isEbtAccount"
                >
                  EBT Account
                </el-checkbox>
              </div>
            </div>
            <div class="row align-items-center">
              <label class="col-sm-3 control-label">Assigned Operator</label>
              <div class="col-sm-7">
                <dropdown-list
                  v-model="instacartInfo.operatorId"
                  filterable
                  clearable
                  :options="operatorOptions"
                />
              </div>
            </div>
            <div class="row">
              <label class="col-sm-3 control-label">Notes</label>
              <div class="col-sm-8">
                <sanitized-textarea
                  id="description"
                  v-model="instacartInfo.notes"
                  placeholder="Notes"
                  autosize
                />
              </div>
            </div>
          </fieldset>
          <hr>
          <div class="form-submit-button-group">
            <p-button
              type="purple"
              round
              native-type="submit"
              :disabled="!isPhoneNumberValid || !isEmailValid"
            >
              {{ actionBtnText }}
            </p-button>

            <p-button
              type="yellow"
              round
              @click="resetInstacartAccountForm"
            >
              Reset
            </p-button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElLoading } from 'element-plus';

import util from '@/Utils/util';
import { countryList } from '@/constants/address';
import { useGlobal } from '@/use/global';
import { useSwalDialog } from '@/use/swalDialog';
import { InstacartCountry } from '@/gql/graphql';
import { InstacartAccount, InstacartAccountInput } from '@/models/instacartAccount';
import { ListType } from '@/models/app';
import { instacartAccountService } from '@/services/instacartAccount';
import { operatorService } from '@/services/operators';

const pageId = 'create-update-instacart-account';
const paginationLimit = 100;

const router = useRouter();

const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();
const { instacartEmail } = useGlobal();

const instacartInfo: Ref<InstacartAccountInput> = ref({
  email: '',
  password: '',
  phoneNumber: '',
  country: InstacartCountry.Us,
  isActive: true,
  isEbtAccount: false,
  notes: '',
  operatorId: null,
});
const backupInstacartAccount: Ref<InstacartAccount | null> = ref(null);

const operatorOptions: Ref<ListType[]> = ref([]);
const isEmailValid = ref(true);

const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(instacartInfo.value.phoneNumber, false));
const actionBtnText = computed(() => (backupInstacartAccount.value?.id ? 'Update' : 'Create'));

const updateEmailValidation = (value: boolean) => {
  isEmailValid.value = value;
};

const loadGroceryOperators = async (currentPage: number = 1) => {
  const offset = (currentPage - 1) * paginationLimit;

  const loading = ElLoading.service({
    lock: true,
    text: 'Loading Operators...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  try {
    const response = await operatorService.fetchOperators({
      roles: ['grocery-operator'],
      isActive: true,
      pagination: {
        offset,
        limit: paginationLimit,
      },
    });

    for (const operator of response.rows) {
      operatorOptions.value.push({
        id: operator.id,
        key: operator.id,
        label: util.getFullName(operator.firstName ?? '', operator.lastName ?? ''),
      });
    }

    if (operatorOptions.value.length < response.count) {
      return loadGroceryOperators(currentPage + 1);
    }
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  } finally {
    loading.close();
  }
};

const getInstacartAccountInfo = async () => {
  const loading = ElLoading.service({
    lock: true,
    text: 'Loading Instacart Account Info...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  try {
    backupInstacartAccount.value = await instacartAccountService.fetchInstacartAccount(instacartEmail.value);

    instacartInfo.value = {
      email: backupInstacartAccount.value?.email,
      phoneNumber: backupInstacartAccount.value?.phoneNumber,
      country: backupInstacartAccount.value?.country,
      isActive: backupInstacartAccount.value?.isActive,
      isEbtAccount: backupInstacartAccount.value?.isEbtAccount,
      notes: backupInstacartAccount.value?.notes,
      operatorId: backupInstacartAccount.value?.operatorId,
    };
  } catch (error) {
    console.log(util.getGraphqlError(error));
  }

  loading.close();
};

const createInstacartAccountInfo = async (postData: InstacartAccountInput) => {
  try {
    await instacartAccountService.createInstacartAccount(postData);

    showSuccessPopupDialog({
      text: 'The Instacart account has been created successfully.',
    });

    router.push({
      name: 'Grocery',
      hash: '#instacart-accounts-table',
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
};

const updateInstacartAccountInfo = async (postData: InstacartAccountInput) => {
  try {
    if (!postData.password) {
      delete postData.password;
    }

    await instacartAccountService.updateInstacartAccount({
      email: backupInstacartAccount.value?.email!,
      data: postData,
    });

    showSuccessPopupDialog({
      text: 'The Instacart account has been updated successfully.',
    });

    router.push({
      name: 'Grocery',
      hash: '#instacart-accounts-table',
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
};

const submitForm = async () => {
  const phoneNumber = util.isPhoneNumberValid(instacartInfo.value.phoneNumber) ? instacartInfo.value.phoneNumber : null;

  const postData: InstacartAccountInput = {
    email: instacartInfo.value.email,
    password: instacartInfo.value.password,
    phoneNumber,
    country: instacartInfo.value.country,
    isActive: instacartInfo.value.isActive,
    isEbtAccount: instacartInfo.value.isEbtAccount,
    notes: instacartInfo.value.notes,
    // Clearing the dropdown yields '' / undefined; the backend needs an explicit null to unassign.
    operatorId: instacartInfo.value.operatorId || null,
  };

  const loading = ElLoading.service({
    lock: true,
    text: backupInstacartAccount.value?.id ? 'Updating Instacart Account...' : 'Creating Instacart Account...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  if (backupInstacartAccount.value) {
    // Update Instacart Account
    await updateInstacartAccountInfo(postData);
  } else {
    // Create Instacart Account
    await createInstacartAccountInfo(postData);
  }

  loading.close();
};

const resetInstacartAccountForm = () => {
  if (backupInstacartAccount.value) {
    instacartInfo.value = {
      email: backupInstacartAccount.value?.email,
      phoneNumber: backupInstacartAccount.value?.phoneNumber,
      country: backupInstacartAccount.value?.country,
      isActive: backupInstacartAccount.value?.isActive,
      isEbtAccount: backupInstacartAccount.value?.isEbtAccount,
      notes: backupInstacartAccount.value?.notes,
      operatorId: backupInstacartAccount.value?.operatorId,
    };
  } else {
    instacartInfo.value = {
      email: '',
      password: '',
      phoneNumber: '',
      country: InstacartCountry.Us,
      isActive: true,
      isEbtAccount: false,
      notes: '',
      operatorId: null,
    };
  }
};

onMounted(async () => {
  await loadGroceryOperators();

  if (instacartEmail.value) {
    getInstacartAccountInfo();
  }
});
</script>
